'use client';

import { PrismicNextLink } from '@prismicio/next';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@radix-ui/react-accordion';
import { Cross1Icon, HamburgerMenuIcon } from '@radix-ui/react-icons';
import { ArrowRightIcon } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { useEffect, useState } from 'react';

import { redirectToSuitableSignUpFunnelStep } from '@/app/[locale]/(legacy)/(auth)/sign-up/action';
import {
  homeLink,
  logo,
  mabilePanelClose,
  mainNavGroupLink,
  mobile,
  mobileAccordionContent,
  mobileAccordionLink,
  mobileAccordionTrigger,
  mobileAdditionalAction,
  mobileAdditionalActions,
  mobilePanel,
  mobilePanelHeader,
} from '@/app/_componentsv2/layout/main/main-header.css';
import { SowefundLogo } from '@/app/_componentsv2/sowefund-logo';
import { User } from '@/domain/user/services/types';
import { shutdownIntercom } from '@/lib/intercom/client';
import Routes from '@/lib/router/routes';
import { MainNavigationDocument } from '@/prismicio-types';

import { logout } from './action';
import { useConnectedMenu } from './connected-menu';

type MobileMenuProps = {
  mainNavigation: MainNavigationDocument;
  hasUserInvestmentToSign: boolean;
  user: User | undefined;
};

export function MobileMenu(props: MobileMenuProps) {
  const { user, mainNavigation } = props;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const t = useTranslations();
  const pathname = usePathname();
  const connectedMenu = useConnectedMenu(props);

  const showCompleteFolderCta =
    !!user && (!user.isFolderCompleted || !user.isIdentityValidated);

  const searchParams =
    pathname !== Routes.Home
      ? `?${new URLSearchParams({ origin: pathname }).toString()}`
      : '';

  const handleLogout = async () => {
    // Here we need to manually call the intercom shutdown, because it must remove its own cookies before logging out the user
    shutdownIntercom();
    await logout();
  };

  // If the pathname change it means we navigated using one of the link.
  // We need to close the menu
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // If the pathname didn't change, but we click on the item matching our current location
  // The user will also expect to close the menu
  const closeMobileMenu = (target: Routes | string) => {
    if (pathname === target) {
      setIsMobileMenuOpen(false);
    }
  };

  const handleCompleteFolder = async () => {
    await redirectToSuitableSignUpFunnelStep();
  };

  // Why we do not only uses the `closeMobileMenu` function ?
  // Because we close the menu BEFORE navigating resulting in a glitchy behavior
  // Closing the menu / Displaying current Page / Moving to other page / Displaying other page

  return (
    <div
      className={mobile}
      data-page-locked={isMobileMenuOpen ? 'true' : undefined}
    >
      <button
        className={mainNavGroupLink}
        aria-label={t('header.menu.toggle-label')}
        onClick={() => setIsMobileMenuOpen(true)}
      >
        <HamburgerMenuIcon />
      </button>
      {isMobileMenuOpen ? (
        <div className={mobilePanel}>
          <div className={mobilePanelHeader}>
            <Link
              className={homeLink}
              href="/"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <SowefundLogo className={logo} />
            </Link>
            <button
              className={mabilePanelClose}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Cross1Icon />
            </button>
          </div>
          <div>
            <Accordion type="single">
              {mainNavigation.data.items.map((item, index) => (
                <AccordionItem key={index} value={`${index}`}>
                  <AccordionTrigger className={mobileAccordionTrigger}>
                    <ArrowRightIcon />
                    {item.link.text}
                  </AccordionTrigger>
                  <AccordionContent className={mobileAccordionContent}>
                    {item.sub_links.map((childrenItem, index) => (
                      <PrismicNextLink
                        className={mobileAccordionLink}
                        key={index}
                        field={childrenItem}
                      />
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ))}
              {user ? (
                <AccordionItem value="user">
                  <AccordionTrigger className={mobileAccordionTrigger}>
                    <ArrowRightIcon />
                    {t('header.menu.my-account')}
                  </AccordionTrigger>
                  <AccordionContent className={mobileAccordionContent}>
                    {connectedMenu.map((item) => (
                      <Link
                        className={mobileAccordionLink}
                        onClick={() => closeMobileMenu(item.path)}
                        key={item.path}
                        href={item.path}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ) : null}
              {showCompleteFolderCta ? (
                <button onClick={() => handleCompleteFolder()}>
                  {t('header.menu.complete-folder')}
                </button>
              ) : null}
            </Accordion>

            <div className={mobileAdditionalActions}>
              {user ? (
                <button
                  className={mobileAdditionalAction}
                  onClick={() => handleLogout()}
                >
                  {t('auth.logout')}
                </button>
              ) : (
                <>
                  <Link
                    className={mobileAdditionalAction}
                    onClick={() => closeMobileMenu(Routes.SignIn)}
                    href={`${Routes.SignIn}${searchParams}`}
                    rel="nofollow"
                  >
                    {t('auth.signIn.title')}
                  </Link>
                  <Link
                    className={mobileAdditionalAction}
                    onClick={() => closeMobileMenu(Routes.SignUpRegister)}
                    href={`${Routes.SignUpRegister}${searchParams}`}
                    rel="nofollow"
                  >
                    {t('auth.signUp.cta')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
